<template>
    <div
        @wheel="handleWheel"
        @mousemove="handleMove"
        @mousedown="mousePressed = true"
        @mouseup="mousePressed = false"
        class="diagram-viewer"
        :style="{ cursor: mousePressed ? 'grabbing' : 'grab' }"
    >
        <div class="wrapper" :style="{ transform }">
            <BlockConnectors
                :blocks="blocks.blocks"
                :metas="blocks.metas"
                :test-state="testState"
                :test-start-block-id="testStartBlockId"
                :goto-preview="gotoPreview"
            />
            <Block
                @openContextMenu="(e) => $emit('openContextMenu', e)"
                v-for="block in blocks.blocks"
                :key="block.id"
                :block="block"
                :metas="blocks.metas"
                :active="selectedBlock === block.id"
                @focusBlock="focusBlock"
                :board="board"
                :storyId="storyId"
                :storyVersion="storyVersion"
                :flattedDiagram="flattedDiagram"
                :movingMode="movingMode"
                :movingBlock="movingBlock"
                :changeMoveMode="changeMoveMode"
                :moveBlock="moveBlock"
                :copyingMode="copyingMode"
                :copyingBlock="copyingBlock"
                :changeCopyMode="changeCopyMode"
                :copyBlock="copyBlock"
                :showNotification="showNotification"
                :dragMode="dragMode"
                :addBlockToDiagram="addBlockToDiagram"
                :draggingType="draggingType"
                :collapsed="board.state.collapsed.includes(block.id)"
                :collapsedAll="board.state.collapsed.includes(block.id)"
                :start-test-from-here="startTestFromHere"
                :test-position="$_.get(testState, 'position')"
                :test-closed="$_.get(testState, 'closed', false)"
                :selected="selectedBlockId === block.id"
                :preview-mode="previewMode"
                :getAllowedBlocks="getAllowedBlocks"
                :showAIKnowledge="showAIKnowledge"
                @gotoHover="onGotoHover"
                @gotoHoverOut="onGotoHoverOut"
                @gotoTarget="onGotoTarget"
            />
        </div>

        <!-- Mini Map -->
        <div class="supsis-chatbot-minimap-container" v-if="minimapVisible && blocks.blocks.length > 5">
            <div class="supsis-chatbot-minimap-content" @click="onMinimapClick">
                <svg :width="minimapWidth" :height="minimapHeight">
                    <defs>
                        <linearGradient id="supsis-gradient-start" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#10b981;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#059669;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="supsis-gradient-ai" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#06b6d4;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#0891b2;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="supsis-gradient-flow" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#8b5cf6;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#7c3aed;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="supsis-gradient-message" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#3b82f6;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#2563eb;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="supsis-gradient-input" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#f59e0b;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#d97706;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="supsis-gradient-attachment" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#f97316;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#ea580c;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="supsis-gradient-condition" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#ef4444;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#dc2626;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="supsis-gradient-success" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#22c55e;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#16a34a;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="supsis-gradient-failure" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#f87171;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#ef4444;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="supsis-gradient-fallback" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#94a3b8;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#64748b;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="supsis-gradient-close" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#4b5563;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#374151;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="supsis-gradient-transfer" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#ec4899;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#db2777;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="supsis-gradient-functions" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#14b8a6;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#0d9488;stop-opacity:1" />
                        </linearGradient>
                        <linearGradient id="supsis-gradient-default" x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" style="stop-color:#9ca3af;stop-opacity:1" />
                            <stop offset="100%" style="stop-color:#6b7280;stop-opacity:1" />
                        </linearGradient>
                    </defs>

                    <!-- Basit önizleme için tüm blokları göster -->
                    <rect
                        v-for="block in blocks.blocks"
                        :key="'supsis-minimap-' + block.id"
                        :x="getMinimapX(blocks.metas[block.id].x)"
                        :y="getMinimapY(blocks.metas[block.id].y)"
                        :width="getMinimapWidth(blocks.metas[block.id].width)"
                        :height="getMinimapHeight(blocks.metas[block.id].height)"
                        :class="['supsis-chatbot-minimap-block', { 'supsis-selected': selectedBlockId === block.id }]"
                        :fill="getBlockColor(block.type)"
                        rx="2"
                        ry="2"
                    />

                    <rect
                        :x="viewportX"
                        :y="viewportY"
                        :width="viewportWidth"
                        :height="viewportHeight"
                        class="supsis-chatbot-minimap-viewport"
                        fill="rgba(59, 130, 246, 0.1)"
                        stroke="#3b82f6"
                        stroke-width="2"
                        stroke-dasharray="4,2"
                        rx="6"
                        ry="6"
                    />
                </svg>
            </div>
        </div>
    </div>
</template>

<script>
import { log2 } from "../../../../../../utils/log2";
import _ from "lodash";
import { mapGetters } from "vuex";
import BlockConnectors from "./BlockConnectors";
import Block from "./Block";
import BLOCK_TYPES from "../../../block.types";
import * as d3 from "d3";
import SYSTEM_PUBSUB from "../../../../../../utils/systemPubSub";
import { filter } from "rxjs/operators";

const log = log2("DiagramViewer");
export default {
    name: "DiagramViewer",
    props: [
        "board",
        "diagram",
        "scale",
        "translate",
        "translating",
        "selectedBlock",
        "navigation",
        "navigateToBlock",
        "storyId",
        "storyVersion",
        "movingMode",
        "movingBlock",
        "changeMoveMode",
        "moveBlock",
        "copyingMode",
        "copyingBlock",
        "changeCopyMode",
        "copyBlock",
        "flattedDiagram",
        "showNotification",
        "dragMode",
        "addBlockToDiagram",
        "draggingType",
        "startTestFromHere",
        "testState",
        "selectedBlockId",
        "customAttributes",
        "previewMode",
        "testStartBlockId",
        "getAllowedBlocks",
    ],
    components: { Block, BlockConnectors },
    data() {
        return {
            mousePressed: false,
            sub: null,
            gotoPreview: null,
            minimapVisible: false,
            minimapWidth: 200,
            minimapHeight: 150,
            minimapUpdateTimer: null,
            cachedMinimapData: null,
            minimapClickThrottle: false,
        };
    },

    computed: {
        ...mapGetters({
            settings: "CB/settings",
        }),

        transform() {
            return `scale(${this.scale}) translate(${this.translate.x}px, ${this.translate.y}px)`;
        },

        // Mini-map computed properties (cached for performance)
        minimapBounds() {
            if (!this.minimapVisible) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
            
            if (!this.blocks.metas || Object.keys(this.blocks.metas).length === 0) {
                return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
            }

            // Cache için key oluştur
            const cacheKey = JSON.stringify(Object.keys(this.blocks.metas));
            if (this.cachedMinimapData && this.cachedMinimapData.cacheKey === cacheKey) {
                return this.cachedMinimapData.bounds;
            }

            const metas = Object.values(this.blocks.metas);
            const minX = Math.min(...metas.map(m => m.x));
            const maxX = Math.max(...metas.map(m => m.x + m.width));
            const minY = Math.min(...metas.map(m => m.y));
            const maxY = Math.max(...metas.map(m => m.y + m.height));

            const bounds = { minX, maxX, minY, maxY };
            
            // Cache'e kaydet
            this.cachedMinimapData = { cacheKey, bounds };
            
            return bounds;
        },

        minimapScale() {
            const bounds = this.minimapBounds;
            const diagramWidth = bounds.maxX - bounds.minX;
            const diagramHeight = bounds.maxY - bounds.minY;

            if (diagramWidth === 0 || diagramHeight === 0) return 1;

            const scaleX = (this.minimapWidth - 16) / diagramWidth;
            const scaleY = (this.minimapHeight - 16) / diagramHeight;

            return Math.min(scaleX, scaleY, 0.5);
        },

        viewportX() {
            const bounds = this.minimapBounds;
            const currentViewX = -this.translate.x / this.scale;
            return (currentViewX - bounds.minX) * this.minimapScale + 8;
        },

        viewportY() {
            const bounds = this.minimapBounds;
            const currentViewY = -this.translate.y / this.scale;
            return (currentViewY - bounds.minY) * this.minimapScale + 8;
        },

        viewportWidth() {
            const viewWidth = window.innerWidth / this.scale;
            return Math.min(viewWidth * this.minimapScale, this.minimapWidth - 16);
        },

        viewportHeight() {
            const viewHeight = window.innerHeight / this.scale;
            return Math.min(viewHeight * this.minimapScale, this.minimapHeight - 16);
        },

        blocks: function () {
            const startPoint = { x: 100, y: 300 };
            const blockSizes = { width: 190, height: 40 };
            const squareBlockSizes = { width: 40, height: 40 };
            const squareBlockTypes = [BLOCK_TYPES.USER_INPUT, BLOCK_TYPES.ATTACHMENT_INPUT];
            const rootBlock = this.navigation.selectedFlowPath
                ? _.get(this.diagram, this.navigation.selectedFlowPath)
                : this.diagram;

            const metas = {
                [rootBlock.id]: {
                    width: squareBlockTypes.includes(rootBlock.type) ? squareBlockSizes.width : blockSizes.width,
                    height: squareBlockTypes.includes(rootBlock.type) ? squareBlockSizes.height : blockSizes.height,
                    name: rootBlock.name,
                    isRoot: true,
                    isSquare: squareBlockTypes.includes(rootBlock.type),
                },
            };
            const rootMeta = metas[rootBlock.id];

            const flatBlock = (block, parentMeta, isRoot) => {
                const isSquare = squareBlockTypes.includes(block.type);
                const sizes = isSquare ? squareBlockSizes : blockSizes;
                const meta = isRoot
                    ? parentMeta
                    : {
                          width: sizes.width,
                          height: sizes.height,
                          name: block.name,
                          isRoot: false,
                          isSquare,
                      };

                metas[block.id] = meta;
                // log("metas", metas);
                const nextFlow = !isRoot && block.type === "FLOW";

                const items = [
                    block,
                    ...(nextFlow ||
                    (this.board.state.collapsed.includes(block.id) && !["START_POINT"].includes(block.type))
                        ? []
                        : block.children
                              .map((childBlock, i) => {
                                  return !!childBlock ? flatBlock(childBlock, meta, false) : null;
                              })
                              .filter(Boolean)),
                ];

                return _.flatten(items);
            };

            const items = _.flatten(flatBlock(rootBlock, rootMeta, true));

            const calculatePositions = () => {
                const root = _.cloneDeep(rootBlock);
                const flatFlows = (block, isRoot = false) => {
                    if (!isRoot && block.type === BLOCK_TYPES.FLOW) block.children = [];
                    block.children = block.children.filter(Boolean);
                    block.children.filter(Boolean).forEach(flatFlows);
                };
                flatFlows(root, true);

                console.log("calculatePositions", root);
                let treemap = d3.tree().nodeSize([blockSizes.height * 2.5, blockSizes.width + 80]);

                let nodes = d3.hierarchy(root, function (d) {
                    return d.children;
                });
                nodes = treemap(nodes);
                // log("nodes", nodes);
                const traverse = (block) => {
                    // log("traverse", block, block.children);
                    if (!!metas[block.data.id]) {
                        metas[block.data.id].x = block.y;
                        metas[block.data.id].y = block.x;
                    }
                    (block.children || []).filter(Boolean).forEach((child) => {
                        traverse(child);
                    });
                };
                traverse(nodes);
            };
            calculatePositions();

            if (!this.translating) this.$emit("blockMetasCalculated", metas);
            const aiAssistantBlock = {
                id: "AI_ASSISTANT",
                name: "",
                type: "AI_ASSISTANT",
                children: [],
            };
            const aiAssistantBlockEnabled = !this.navigation.selectedFlow;
            if (aiAssistantBlockEnabled) {
                metas[aiAssistantBlock.id] = {
                    width: 190,
                    height: 40,
                    name: "",
                    isRoot: false,
                    isSquare: false,
                    x: 0,
                    y: 100,
                };
            }

            log("metas", {
                metas,
                items,
                aiAssistantBlock,
            });
            return {
                blocks: aiAssistantBlockEnabled ? [aiAssistantBlock, ...items] : items,
                metas,
            };
        },
    },
    methods: {
        showAIKnowledge() {
            this.$emit("showAIKnowledge");
        },

        // Mini-map methods
        handleMinimapToggle() {
            this.minimapVisible = !this.minimapVisible;
            if (!this.minimapVisible) {
                // Mini-map kapatıldığında cache'i temizle
                this.cachedMinimapData = null;
            }
        },

        // Optimized minimap update with requestAnimationFrame
        debouncedMinimapUpdate() {
            if (this.minimapUpdateTimer) {
                clearTimeout(this.minimapUpdateTimer);
            }
            this.minimapUpdateTimer = setTimeout(() => {
                if (this.minimapVisible) {
                    requestAnimationFrame(() => {
                        this.$forceUpdate();
                    });
                }
            }, 150); // Daha uzun debounce
        },

        getMinimapX(x) {
            const bounds = this.minimapBounds;
            return (x - bounds.minX) * this.minimapScale + 8;
        },

        getMinimapY(y) {
            const bounds = this.minimapBounds;
            return (y - bounds.minY) * this.minimapScale + 8;
        },

        getMinimapWidth(width) {
            return Math.max(width * this.minimapScale, 2);
        },

        getMinimapHeight(height) {
            return Math.max(height * this.minimapScale, 2);
        },

        getBlockColor(blockType) {
            const colors = {
                'START_POINT': 'url(#supsis-gradient-start)',
                'AI_ASSISTANT': 'url(#supsis-gradient-ai)',
                'FLOW': 'url(#supsis-gradient-flow)',
                'MESSAGE': 'url(#supsis-gradient-message)',
                'USER_INPUT': 'url(#supsis-gradient-input)',
                'ATTACHMENT_INPUT': 'url(#supsis-gradient-attachment)',
                'CONDITION': 'url(#supsis-gradient-condition)',
                'SUCCESS': 'url(#supsis-gradient-success)',
                'FAILURE': 'url(#supsis-gradient-failure)',
                'FALLBACK': 'url(#supsis-gradient-fallback)',
                'CLOSE_CHAT': 'url(#supsis-gradient-close)',
                'TRANSFER_CHAT': 'url(#supsis-gradient-transfer)',
                'AI_FUNCTIONS': 'url(#supsis-gradient-functions)',
                'DEFAULT_FALLBACK': 'url(#supsis-gradient-default)'
            };
            return colors[blockType] || 'url(#supsis-gradient-default)';
        },

        onMinimapClick(event) {
            // Performans için agresif throttle
            if (this.minimapClickThrottle) return;
            this.minimapClickThrottle = true;
            setTimeout(() => { this.minimapClickThrottle = false; }, 300);

            const rect = event.currentTarget.getBoundingClientRect();
            const clickX = event.clientX - rect.left - 8;
            const clickY = event.clientY - rect.top - 8;

            const bounds = this.minimapBounds;
            const realX = (clickX / this.minimapScale) + bounds.minX;
            const realY = (clickY / this.minimapScale) + bounds.minY;

            const newTranslateX = -realX * this.scale + window.innerWidth / 2;
            const newTranslateY = -realY * this.scale + window.innerHeight / 2;

            this.$emit("setTranslate", {
                x: newTranslateX,
                y: newTranslateY
            });
        },
        onGotoHover(payload) {
            this.gotoPreview = payload;
        },
        onGotoHoverOut() {
            this.gotoPreview = null;
        },
        onGotoTarget(targetId) {
            try {
                const targetBlock = this.flattedDiagram.find((b) => b.id === targetId);
                if (!targetBlock) return;
                const findParentContainer = (block) => {
                    const parent = block.parent;
                    if (!parent) return null;
                    if ([BLOCK_TYPES.FLOW, BLOCK_TYPES.START_POINT].includes(parent.type)) return parent;
                    return findParentContainer(parent);
                };
                const parentContainer = findParentContainer(targetBlock);
                if (parentContainer && parentContainer.type === BLOCK_TYPES.FLOW) {
                    if (this.navigation.selectedFlow !== parentContainer.id) {
                        this.navigateToBlock(parentContainer);
                    }
                } else {
                    this.navigateToBlock(null);
                }
                setTimeout(() => {
                    SYSTEM_PUBSUB.publish(SYSTEM_PUBSUB.MESSAGE_TYPES.CB_FOCUS_BLOCK, {
                        blockId: targetId,
                    });
                }, 400);
            } catch (e) {}
        },
        handleWheel(e) {
            e.preventDefault();
            const settings = this.settings(this.storyId);
            log("handleWheel", e);
            log("handleWheel", { x: -e.deltaX, y: -e.deltaY, ctrlKey: e.ctrlKey, settings });
            if (settings.navigation === "mouse" || e.ctrlKey) {
                // this.$emit("updateScale", e.deltaY * 0.05);
                const currentScale = this.scale;

                let value = Math.min(200, Math.abs(e.deltaY));
                //scale 200  to 0.5 - 1.5
                let newDelta = (value / 200 + 0.5) / 10;
                newDelta = e.deltaY <= 0 ? newDelta : newDelta * -1;
                const nextScale = currentScale + newDelta;
                log("updateScale calling", {
                    deltaY: e.deltaY,
                    currentScale,
                    value,
                    newDelta,
                    nextScale,
                });
                // const smoothScale = (oldScale, newScale) => {
                //     const diff = newScale - oldScale;
                //     const step = diff / 10;
                //
                //     for (let i = 0; i < 10; i++) {
                //         setTimeout(() => {
                //             this.$emit("updateScale", oldScale + step * (i + 1));
                //         }, 20);
                //     }
                // };
                // smoothScale(currentScale, nextScale);
                this.$emit("updateScale", nextScale);
            } else {
                log("updateTranslate calling");
                this.$emit("updateTranslate", { x: -e.deltaX, y: -e.deltaY });
            }
        },
        handleMove(e) {
            let movement = {
                x: e.movementX,
                y: e.movementY,
            };
            e.preventDefault();
            if (this.mousePressed) this.$emit("updateTranslate", movement);
        },
        focusBlock(e, block) {
            log("focusBlock block", block);
            if (
                [
                    BLOCK_TYPES.START_POINT,
                    BLOCK_TYPES.DEFAULT_FALLBACK,
                    BLOCK_TYPES.SUCCESS,
                    BLOCK_TYPES.FAILURE,
                    BLOCK_TYPES.FALLBACK,
                    BLOCK_TYPES.CLOSE_CHAT,
                    BLOCK_TYPES.TRANSFER_CHAT,
                    BLOCK_TYPES.AI_FUNCTIONS,
                ].includes(block.type)
            )
                return;
            if (block.type === "FLOW") {
                this.navigateToBlock(block);
                return;
            }
            this.$emit("openModal", block);
            log("focusBlock x, y");
            const { x, y } = this.blocks.metas[block.id];
            this.$emit("setTranslate", {
                x: -x + window.innerWidth / 2 - 250,
                y: -y + window.innerHeight / 2 - 50,
            });
        },
    },
    mounted() {
        this.$emit("blocks-metas", this.blocks.metas);
        this.sub = SYSTEM_PUBSUB.stream
            .pipe(filter((x) => x.type === SYSTEM_PUBSUB.MESSAGE_TYPES.CB_FOCUS_BLOCK))
            .subscribe(({ payload }) => {
                log("mounted blocks focusBlock start");
                if (!!this.blocks.metas[payload.blockId]) {
                    log("mounted blocks focusBlock start if", {
                        b: this.blocks,
                        m: this.blocks.metas,
                        mp: this.blocks.metas[payload.blockId],
                        p: payload,
                    });
                    const { x, y } = this.blocks.metas[payload.blockId];
                    log("mounted blocks focusBlock x, y", { x, y });
                    this.$emit("setTranslate", {
                        x: -x + window.innerWidth / 2 - 250,
                        y: -y + window.innerHeight / 2 - 50,
                    });
                }
            });
    },
    updated() {
        log("updated");
    },
    beforeDestroy() {
        this.sub.unsubscribe();
        if (this.minimapUpdateTimer) {
            clearTimeout(this.minimapUpdateTimer);
        }
    },
    watch: {
        board: {
            handler() {
                log("updated board");
            },
            deep: true,
        },
        diagram: {
            handler() {
                log("updated diagram");
            },
            deep: true,
        },
        scale: {
            handler() {
                log("updated scale");
                if (this.minimapVisible) {
                    this.debouncedMinimapUpdate();
                }
            },
            deep: true,
        },
        translate: {
            handler() {
                log("updated translate");
                if (this.minimapVisible) {
                    this.debouncedMinimapUpdate();
                }
            },
            deep: true,
        },
        selectedBlock: {
            handler() {
                log("updated selectedBlock");
            },
            deep: true,
        },
        navigation: {
            handler(oldVal, newVal) {
                log("updated navigation", { oldVal, newVal });
            },
            deep: true,
        },
        movingMode: {
            handler() {
                log("updated movingMode");
            },
        },
        movingBlock: {
            handler() {
                log("updated movingBlock");
            },
            deep: true,
        },
        copyingMode: {
            handler() {
                log("updated copyingMode");
            },
        },
        copyingBlock: {
            handler() {
                log("updated copyingBlock");
            },
            deep: true,
        },
        changeCopyMode: {
            handler() {
                log("updated changeCopyMode");
            },
        },
        copyBlock: {
            handler() {
                log("updated copyBlock");
            },
        },
        flattedDiagram: {
            handler() {
                log("updated flattedDiagram");
            },
        },
        dragMode: {
            handler() {
                log("updated dragMode");
            },
        },
        draggingType: {
            handler() {
                log("updated draggingType");
            },
        },
        testState: {
            handler() {
                log("updated testState");
            },
            deep: true,
        },
        selectedBlockId: {
            handler() {
                log("updated selectedBlockId");
            },
        },
        customAttributes: {
            handler() {
                log("updated customAttributes");
            },
            deep: true,
        },
        previewMode: {
            handler() {
                log("updated previewMode");
            },
        },
        testStartBlockId: {
            handler() {
                log("updated testStartBlockId");
            },
        },
        getAllowedBlocks: {
            handler() {
                log("updated getAllowedBlocks");
            },
            deep: true,
        },
    },
};
</script>

<style lang="scss" scoped>
.diagram-viewer {
    //height: calc(100vh - 50px);
    height: calc(100vh - 55px);
    background-image: linear-gradient(rgba(214, 214, 219, 0.2) 1px, transparent 1px),
        linear-gradient(to right, rgba(214, 214, 219, 0.4) 1px, rgba(139, 140, 140, 0.1) 1px) !important;
    background-size: 20px 20px;
    position: relative;

    .wrapper {
        height: 100%;
        width: 100%;
    }

    // Mini-map unique styles (performance optimized)
    .supsis-chatbot-minimap-container {
        position: absolute;
        bottom: 70px;
        left: 15px;
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%);
        border: 1px solid rgba(226, 232, 240, 0.8);
        border-radius: 16px;
        box-shadow:
            0 20px 25px -5px rgba(0, 0, 0, 0.1),
            0 10px 10px -5px rgba(0, 0, 0, 0.04),
            0 0 0 1px rgba(255, 255, 255, 0.05);
        z-index: 1000;
        backdrop-filter: blur(20px) saturate(180%);
        transition: transform 0.2s ease, opacity 0.2s ease;
        overflow: hidden;
        will-change: transform, opacity;
        transform: translateZ(0); // Hardware acceleration
        contain: layout style paint;

        &:hover {
            transform: translateY(-2px);
            box-shadow:
                0 25px 50px -12px rgba(0, 0, 0, 0.15),
                0 20px 20px -5px rgba(0, 0, 0, 0.06),
                0 0 0 1px rgba(255, 255, 255, 0.1);
        }


        .supsis-chatbot-minimap-content {
            padding: 8px;
            cursor: crosshair;
            background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
            border-radius: 16px;
            will-change: transform;
            contain: layout style paint;
            transform: translateZ(0);

            svg {
                display: block;
                background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
                border: 2px solid rgba(148, 163, 184, 0.2);
                border-radius: 12px;
                box-shadow:
                    inset 0 2px 4px rgba(0, 0, 0, 0.06),
                    0 1px 3px rgba(0, 0, 0, 0.1);
                transition: all 0.2s ease;

                &:hover {
                    border-color: rgba(99, 102, 241, 0.3);
                    box-shadow:
                        inset 0 2px 4px rgba(0, 0, 0, 0.06),
                        0 4px 6px rgba(0, 0, 0, 0.1),
                        0 0 0 3px rgba(99, 102, 241, 0.1);
                }
            }

            .supsis-chatbot-minimap-block {
                stroke: rgba(255, 255, 255, 0.6);
                stroke-width: 0.3;
                opacity: 0.8;
                transition: none; // Transition'ları kaldır
                will-change: auto; // will-change'i kaldır

                &:hover {
                    opacity: 0.9; // Minimal hover efekti
                }

                &.supsis-selected {
                    opacity: 1;
                    stroke: #3b82f6;
                    stroke-width: 1;
                    // Animasyonları kaldır
                }
            }

            .supsis-chatbot-minimap-viewport {
                pointer-events: none;
                opacity: 0.6;
                stroke: #3b82f6;
                stroke-width: 1;
                stroke-dasharray: 2,1;
                fill: rgba(59, 130, 246, 0.05);
                // Animasyonları kaldır
            }
        }
    }

    // Animasyonları kaldırdık - performans için
}
</style>
